export const HOURS_IN_DAY = 24

/** The backend accepts "24:00" as the end of a window; LocalTime could not express it, minutes can. */
export const END_OF_DAY = '24:00'

/**
 * Days of week of a schedule. The backend accepts and returns numbers 1-7 (1 = Monday),
 * matching java.time.DayOfWeek.getValue().
 *
 * `key` points at the existing `device.days.*` keys, abbreviations live under `scheduler.daysShort.<value>` -
 * never hardcode a label here, it would survive a language switch.
 */
export const DAYS = [
  { value: 1, key: 'Monday' },
  { value: 2, key: 'Tuesday' },
  { value: 3, key: 'Wednesday' },
  { value: 4, key: 'Thursday' },
  { value: 5, key: 'Friday' },
  { value: 6, key: 'Saturday' },
  { value: 7, key: 'Sunday' }
]

export const WEEKDAYS = [1, 2, 3, 4, 5]
export const WEEKEND = [6, 7]
export const ALL_DAYS = [1, 2, 3, 4, 5, 6, 7]

export function sortDays(days) {
  return (days || []).slice().sort((a, b) => a - b)
}

/** "07:30" -> 7.5 */
export function toDecimalHours(time) {
  if (!time) return null

  const parts = String(time).split(':')
  return Number(parts[0]) + Number(parts[1] || 0) / 60
}

/**
 * A window crossing midnight (22:00-02:00) occupies [22:00, 24:00) on its own day and [00:00, 02:00)
 * on the next one; its days of week name the day it STARTS. Expanding it into per-day ranges keeps every
 * other calculation as plain interval arithmetic.
 *
 * Mirrors WindowSegments on the backend - the two must never disagree.
 */
export function segmentsOn(window, day) {
  const from = toDecimalHours(window.startTime)
  const to = toDecimalHours(window.endTime)
  const crosses = to < from
  const days = window.daysOfWeek || []
  const previousDay = day === 1 ? 7 : day - 1
  const segments = []

  if (days.indexOf(day) !== -1) segments.push({ from, to: crosses ? HOURS_IN_DAY : to, window })
  if (crosses && days.indexOf(previousDay) !== -1) segments.push({ from: 0, to, window })

  return segments
}

/** A collision only counts on minutes of the same calendar day - see segmentsOn. */
export function collides(windowA, windowB) {
  for (let day = 1; day <= 7; day++) {
    const a = segmentsOn(windowA, day)
    const b = segmentsOn(windowB, day)
    if (a.some((first) => b.some((second) => first.from < second.to && second.from < first.to))) return true
  }

  return false
}

/**
 * The whole day as a sequence of blocks: windows and the gaps between them. A gap carries no window -
 * in a gap the screen shows the base application, so it has to be drawn, not left blank.
 */
export function windowsAndGaps(windows, days) {
  const blocks = []
  let cursor = 0

  const segments = []
  ;(days || []).forEach((day) => (windows || []).forEach((window) => segments.push(...segmentsOn(window, day))))

  segments
    .sort((a, b) => a.from - b.from)
    .forEach((block) => {
      if (block.from > cursor) blocks.push({ from: cursor, to: block.from, window: null })
      if (block.to > cursor) blocks.push({ from: Math.max(block.from, cursor), to: block.to, window: block.window })
      cursor = Math.max(cursor, block.to)
    })

  if (cursor < HOURS_IN_DAY) blocks.push({ from: cursor, to: HOURS_IN_DAY, window: null })

  return blocks
}

export function freeIntervals(windows, days) {
  return windowsAndGaps(windows, days).filter((block) => !block.window)
}

export function formatHours(value) {
  const hours = Math.floor(value)
  const minutes = Math.round((value - hours) * 60)
  return `${hours < 10 ? '0' : ''}${hours}:${minutes < 10 ? '0' : ''}${minutes}`
}
